import { useEffect, useRef } from "react";

import {
  LADO_MAP,
  LIMITE_DESFAZER,
  TX_TIPO_MAP,
  type MovimentoConciliado,
} from "../../lib/conciliacao/tipos";
import { formatarDataISO } from "../../lib/formato/data";
import { formatarDecimal } from "../../lib/formato/decimal";

/*
 * Passo de confirmacao do DESFAZER conciliacao (FE-4 — Tela 4 §10.5).
 *
 * Combina a base de modal do `ConfirmacaoConciliacao` (role=dialog, overlay,
 * foco, Escape) com a textarea de JUSTIFICATIVA do `ModalReagendar`: desfazer e
 * uma acao de EFEITO (reverte o movimento + recalcula a projecao) e exige uma
 * justificativa com >= `LIMITE_DESFAZER` chars apos `.trim()` (mesmo gate do
 * backend, 422 abaixo disso).
 *
 * Componente PURO de apresentacao: NAO conhece o hook de mutacao. O orquestrador
 * (`TelaDesfazarConciliacao`) controla a `justificativa` e injeta os callbacks e o
 * estado `desfazendo`. Enquanto `desfazendo`, os botoes desabilitam, o Confirmar
 * vira "Desfazendo…" e o Escape NAO fecha (acao em voo).
 */

const SEM_VALOR = "—";

/** Consulta um mapa de rotulos `as const`, com fallback ao valor cru. */
function rotular(mapa: Record<string, string>, chave: string): string {
  return mapa[chave] ?? chave;
}

interface ModalDesfazarConciliacaoProps {
  movimento: MovimentoConciliado;
  /** Valor controlado da justificativa (estado vive na pagina). */
  justificativa: string;
  /** Notifica a digitacao da justificativa. */
  aoMudarJustificativa: (valor: string) => void;
  /** Dispara a mutacao real (POST). So o Confirmar DESTE modal o chama. */
  aoConfirmar: () => void;
  /** Fecha o passo sem efeito de escrita. */
  aoCancelar: () => void;
  /** Mutacao em voo: desabilita os botoes e troca o rotulo do Confirmar. */
  desfazendo: boolean;
  /**
   * Mensagem de erro de negocio a exibir DENTRO do modal (422/503/demais), para
   * o operador ver e ajustar/repetir. Texto ja generico/fixo (NUNCA o corpo do
   * servidor); `null` = sem erro. O 409 fecha o modal e exibe na pagina.
   */
  erro: string | null;
}

export function ModalDesfazarConciliacao({
  movimento,
  justificativa,
  aoMudarJustificativa,
  aoConfirmar,
  aoCancelar,
  desfazendo,
  erro,
}: ModalDesfazarConciliacaoProps): JSX.Element {
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Move o foco para a textarea ao abrir (acessibilidade de modal).
  useEffect(() => {
    textareaRef.current?.focus();
  }, []);

  const { transacao, titulo } = movimento;
  const justificativaValida = justificativa.trim().length >= LIMITE_DESFAZER;
  const confirmarDesabilitado = !justificativaValida || desfazendo;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="titulo-desfazer-conciliacao"
      onKeyDown={(evento) => {
        if (evento.key === "Escape" && !desfazendo) {
          aoCancelar();
        }
      }}
      className="fixed inset-0 z-50 flex items-center justify-center bg-off-black/50 p-4"
    >
      <div className="flex w-full max-w-lg flex-col gap-4 rounded-lg border border-platinum bg-snow p-6 font-corpo text-base text-off-black">
        <h2
          id="titulo-desfazer-conciliacao"
          className="font-titulo text-xl text-off-black"
        >
          Desfazer conciliação
        </h2>

        <p>
          Esta ação reverte o movimento conciliado e recalcula a projeção da
          empresa.
        </p>

        <dl className="flex flex-col gap-3">
          <div className="flex flex-col gap-1">
            <dt className="font-subtitulo">Transação</dt>
            <dd>
              <span className="font-subtitulo">
                {formatarDecimal(transacao.valor)}
              </span>{" "}
              · {formatarDataISO(transacao.data)} ·{" "}
              {rotular(TX_TIPO_MAP, transacao.tipo)} ·{" "}
              {transacao.cnpj_masc ?? SEM_VALOR}
            </dd>
          </div>
          <div className="flex flex-col gap-1">
            <dt className="font-subtitulo">Título</dt>
            <dd>
              <span className="font-subtitulo">
                {formatarDecimal(titulo.valor)}
              </span>{" "}
              · {formatarDataISO(titulo.data_prevista)} ·{" "}
              {rotular(LADO_MAP, titulo.lado)} ·{" "}
              {titulo.cnpj_masc ?? SEM_VALOR}
            </dd>
          </div>
        </dl>

        <label className="flex flex-col gap-1">
          <span className="font-subtitulo">
            Justificativa (mínimo 10 caracteres)
          </span>
          <textarea
            ref={textareaRef}
            value={justificativa}
            onChange={(evento) => aoMudarJustificativa(evento.target.value)}
            disabled={desfazendo}
            rows={4}
            className="rounded-md border border-platinum bg-snow px-2 py-1 text-off-black disabled:opacity-60"
          />
        </label>

        {erro !== null && (
          <p role="alert" className="font-corpo text-base text-flame">
            {erro}
          </p>
        )}

        <div className="flex justify-end gap-3">
          <button
            type="button"
            onClick={aoCancelar}
            disabled={desfazendo}
            className="rounded-md border border-platinum bg-snow px-4 py-1 text-off-black disabled:opacity-60"
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={aoConfirmar}
            disabled={confirmarDesabilitado}
            className="rounded-md border border-flame bg-flame px-4 py-1 text-snow disabled:opacity-60"
          >
            {desfazendo ? "Desfazendo…" : "Confirmar"}
          </button>
        </div>
      </div>
    </div>
  );
}
